import { useOrgChartProjects } from './useOrgChartProjects';
import { flattenAreas, memberNode, NODE_WIDTHS, NODE_HEIGHTS } from './useOrgChartData';
import type { OrgChartProjectNodeDto, OrgChartMemberNodeDto } from '../types';
import type { TreeNodeDef } from '@/shared/components/custom/tree-chart';
import type { OrgRootNodeData } from './useOrgChartData';

// ─── Node data shape ──────────────────────────────────────────────────────────

export interface OrgProjectNodeData extends Record<string, unknown> {
  projectId: string;
  name: string | null;
  status: string | null;
  color: string | null;
  areaCount: number;
  memberCount: number;
  unassignedCount: number;
  hasChildren: boolean;
}

// ─── Tree flattening ─────────────────────────────────────────────────────────

/**
 * The same organizational area can be linked to more than one project.
 * `flattenAreas` (shared with the "Organización" tab, where an area never
 * repeats within the tree) ids area nodes by `area.id` alone, so two
 * projects sharing an area would emit two TreeNodeDef with the same id —
 * `computeTreeLayout` indexes by id, so one silently overwrites the other
 * and their members merge under a single area node. Scoping every area id
 * (and re-linking its children's parentId) to the owning project keeps each
 * project's subtree independent.
 */
function scopeAreaIdsToProject(nodes: TreeNodeDef[], projectNodeId: string): TreeNodeDef[] {
  const scopedId = new Map<string, string>();
  for (const node of nodes) {
    if (node.type === 'org-area') {
      scopedId.set(node.id, `${projectNodeId}-area-${node.id}`);
    }
  }

  return nodes.map((node) => ({
    ...node,
    id: scopedId.get(node.id) ?? node.id,
    parentId: node.parentId !== null ? (scopedId.get(node.parentId) ?? node.parentId) : null,
  }));
}

function flattenProjects(
  projects: OrgChartProjectNodeDto[],
  parentId: string,
  search: string,
): TreeNodeDef[] {
  const nodes: TreeNodeDef[] = [];

  for (const project of projects) {
    if (!project.id) continue;

    const areas = project.areas ?? [];
    const unassignedMembers = project.unassignedMembers ?? [];
    const projectNodeId = `project-${project.id}`;

    nodes.push({
      id: projectNodeId,
      parentId,
      type: 'org-project',
      width: NODE_WIDTHS['org-project'],
      height: NODE_HEIGHTS['org-project'],
      data: {
        projectId: project.id,
        name: project.name,
        status: project.status,
        color: project.color,
        areaCount: project.areaCount,
        memberCount: project.memberCount,
        unassignedCount: unassignedMembers.length,
        hasChildren: areas.length > 0 || unassignedMembers.length > 0,
      } satisfies OrgProjectNodeData,
    });

    nodes.push(...scopeAreaIdsToProject(flattenAreas(areas, projectNodeId, search), projectNodeId));

    for (const member of unassignedMembers.filter(
      (m): m is OrgChartMemberNodeDto & { id: string } => m.id !== null,
    )) {
      nodes.push(memberNode(member, projectNodeId, search));
    }
  }

  return nodes;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

interface OrgChartProjectsDataResult {
  nodes: TreeNodeDef[];
  isLoading: boolean;
  error: Error | null;
  totalMembers: number;
}

export function useOrgChartProjectsData(
  searchQuery: string,
  enabled = true,
): OrgChartProjectsDataResult {
  const { data, isLoading, error } = useOrgChartProjects(enabled);

  if (isLoading || !data) {
    return { nodes: [], isLoading, error: error as Error | null, totalMembers: 0 };
  }

  const projects = data.projects ?? [];

  if (projects.length === 0) {
    return { nodes: [], isLoading: false, error: null, totalMembers: data.totalMembers };
  }

  const search = searchQuery.trim().toLowerCase();
  const nodeDefs: TreeNodeDef[] = [];

  nodeDefs.push({
    id: 'root',
    parentId: null,
    type: 'org-root',
    width: NODE_WIDTHS['org-root'],
    height: NODE_HEIGHTS['org-root'],
    data: {
      orgName: data.tenantName ?? '',
      memberCount: data.totalMembers,
      projectCount: data.totalProjects,
    } satisfies OrgRootNodeData,
  });

  nodeDefs.push(...flattenProjects(projects, 'root', search));

  return {
    nodes: nodeDefs,
    isLoading: false,
    error: null,
    totalMembers: data.totalMembers,
  };
}
